import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { DeltaChip } from '../DeltaChip';
import { ReportCard } from '../ReportCard';
import { CreativeCell } from './CreativeCell';
import type { PerformanceReport, Performer } from './data/api';
import { compactCurrency, formatKpi, type KpiDescriptor, spendRatio } from './data/format';

interface PerformerTableProps {
  label: string;
  rows: Performer[];
  kpi: KpiDescriptor;
  currencySymbol: string;
  totalSpend: number;
  onOpenCreative: (creativeId: string) => void;
}

function PerformerTable({
  label,
  rows,
  kpi,
  currencySymbol,
  totalSpend,
  onOpenCreative,
}: PerformerTableProps): ReactNode {
  return (
    <ReportCard label={label}>
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          No creative cleared the band this period.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Creative</TableCell>
              <TableCell align="right">Spend</TableCell>
              <TableCell align="right">Spend ratio</TableCell>
              <TableCell align="right">{kpi.label}</TableCell>
              <TableCell align="right">vs account</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((performer) => (
              <TableRow key={performer.assetId}>
                <CreativeCell row={performer} onOpen={onOpenCreative} />
                <TableCell align="right">
                  {compactCurrency(performer.spend, currencySymbol)}
                </TableCell>
                <TableCell align="right">{spendRatio(performer.spend, totalSpend)}</TableCell>
                <TableCell align="right">
                  {formatKpi(performer.kpiValue, kpi, currencySymbol)}
                </TableCell>
                <TableCell align="right">
                  <DeltaChip value={performer.vsAccount} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </ReportCard>
  );
}

export function Performers({
  report,
  onOpenCreative,
}: {
  report: PerformanceReport;
  onOpenCreative: (creativeId: string) => void;
}): ReactNode {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
        gap: 4,
      }}
    >
      <PerformerTable
        label="Top performers"
        rows={report.performers.top}
        kpi={report.kpi}
        currencySymbol={report.currencySymbol}
        totalSpend={report.periods.current.spend}
        onOpenCreative={onOpenCreative}
      />
      <PerformerTable
        label="Underperformers"
        rows={report.performers.low}
        kpi={report.kpi}
        currencySymbol={report.currencySymbol}
        totalSpend={report.periods.current.spend}
        onOpenCreative={onOpenCreative}
      />
    </Box>
  );
}
